/**
 * exportSignatures — get the on-device signature store out of the browser as
 * `{docNum}.png` files.
 *
 * (2026-08-03) Companion to signatureStore.ts. The manual-checkout-report tool
 * embeds a ticket's signature from a local `{docNum}.png` when S3 has none; this
 * produces exactly those files from the signatures the PWA retained on-device.
 *
 * Two output paths:
 *   - File System Access API (Chrome/Edge): the operator picks a folder and we
 *     write each `{docNum}.png` straight into it — ideal for dropping into the
 *     report tool's `signatures/` directory.
 *   - Fallback (other browsers): each PNG is downloaded individually.
 */
import { signatureStore, StoredSignature } from './signatureStore';

export interface ExportItem {
  /** File name, always `{docNum}.png`. */
  name: string;
  /** The signature PNG as a data URL. */
  dataUrl: string;
}

export interface ExportResult {
  /** Files successfully written/downloaded. */
  count: number;
  /** How they were delivered. */
  method: 'directory' | 'download' | 'none';
  /** True when the user dismissed the directory picker (not an error). */
  cancelled?: boolean;
  /** Files that failed to write/download (present only when > 0). */
  failed?: number;
}

/**
 * Pure: expand stored records into one export entry per docNum. A single record
 * can cover several docNums (one signature per batch checkout), so each gets its
 * own file. Deduplicates by file name, keeping the newest by timestamp. Records
 * with no docNums fall back to their record id as the name.
 * Exposed separately so the fan-out/dedup logic is unit-testable without IO.
 */
export function planExports(records: StoredSignature[]): ExportItem[] {
  const byName = new Map<string, { name: string; dataUrl: string; timestamp: number }>();
  for (const r of records) {
    const keys = r.docNums && r.docNums.length ? r.docNums : [r.id];
    for (const key of keys) {
      const name = `${key}.png`;
      const existing = byName.get(name);
      if (!existing || r.timestamp > existing.timestamp) {
        byName.set(name, { name, dataUrl: r.signature, timestamp: r.timestamp });
      }
    }
  }
  return Array.from(byName.values()).map(({ name, dataUrl }) => ({ name, dataUrl }));
}

/** Decode a PNG data URL into a Blob. Throws on a malformed data URL. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) throw new Error('Malformed data URL');
  const meta = dataUrl.slice(0, comma);
  const b64 = dataUrl.slice(comma + 1);
  const mimeMatch = meta.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Feature-detect the File System Access directory picker. */
function hasDirectoryPicker(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

/**
 * Export every on-device signature as `{docNum}.png`. Prefers writing into a
 * user-picked folder; otherwise downloads each file. Returns a summary; a
 * cancelled folder pick resolves with `{ count: 0, method: 'none', cancelled }`
 * rather than throwing.
 */
export async function exportStoredSignatures(): Promise<ExportResult> {
  const records = await signatureStore.getAll();
  const items = planExports(records);
  if (items.length === 0) return { count: 0, method: 'none' };

  if (hasDirectoryPicker()) {
    let dirHandle: any;
    try {
      // Requires a user gesture (called from a button click).
      dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
    } catch (err: any) {
      // AbortError = user dismissed the picker; treat as a clean cancel.
      if (err && err.name === 'AbortError') return { count: 0, method: 'none', cancelled: true };
      throw err;
    }
    let written = 0;
    let failed = 0;
    for (const item of items) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const fileHandle = await dirHandle.getFileHandle(item.name, { create: true });
        // eslint-disable-next-line no-await-in-loop
        const writable = await fileHandle.createWritable();
        // eslint-disable-next-line no-await-in-loop
        await writable.write(dataUrlToBlob(item.dataUrl));
        // eslint-disable-next-line no-await-in-loop
        await writable.close();
        written += 1;
      } catch (err) {
        // Reason: one bad file (filesystem-unsafe docNum, quota, transient FS
        // error) must not abort the rest of the export — skip it and continue.
        failed += 1;
        console.error(`Failed to write signature file "${item.name}":`, err);
      }
    }
    return { count: written, method: 'directory', ...(failed ? { failed } : {}) };
  }

  // Fallback: trigger a download per file.
  let downloaded = 0;
  let failed = 0;
  for (const item of items) {
    try {
      const url = URL.createObjectURL(dataUrlToBlob(item.dataUrl));
      const a = document.createElement('a');
      a.href = url;
      a.download = item.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Reason: revoke on a later tick — revoking the object URL synchronously
      // right after click can cancel the download mid-flight in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      downloaded += 1;
    } catch (err) {
      failed += 1;
      console.error(`Failed to download signature file "${item.name}":`, err);
    }
  }
  return { count: downloaded, method: 'download', ...(failed ? { failed } : {}) };
}
